import React, { useState } from 'react';
import { useAppStore } from '../../stores/useAppStore';
import { useRestStore } from '../../stores/useRestStore';
import './TimerOverlay.css';

interface TimerOverlayProps {
  visible: boolean;
  onClose: () => void;
  timerDisplay: string;
  progressPercent: number;
  isRunning: boolean;
  phase: 'work' | 'break' | null;
  onStart: () => void;
  onStop: () => void;
}

const WORK_PRESETS = [5, 15, 25, 45];
const BREAK_PRESETS = [3, 5, 10];

interface SettingsPanelProps {
  pomodoroSettings: {
    autoStartBreaks: boolean;
    autoStartPomodoros: boolean;
    soundEnabled: boolean;
    notificationEnabled: boolean;
  };
  updatePomodoroSettings: (s: Partial<SettingsPanelProps['pomodoroSettings']>) => void;
  restSettings: {
    dailyGoalMinutes: number;
  };
  updateRestSettings: (s: Partial<SettingsPanelProps['restSettings']>) => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ pomodoroSettings, updatePomodoroSettings, restSettings, updateRestSettings }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="timer-overlay__settings-wrap">
      <button
        className={`timer-overlay__settings-toggle ${open ? 'timer-overlay__settings-toggle--open' : ''}`}
        onClick={() => setOpen(!open)}
      >
        <svg className="timer-overlay__gear-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="1.5" />
          <path d="M13.7 2.5h-3.4l-.5 2.1a7.5 7.5 0 0 0-2 1.1L5.8 4.9l-1.7 3 1.5 1.5a7.5 7.5 0 0 0 0 2.2l-1.5 1.5 1.7 3 2-0.8a7.5 7.5 0 0 0 2 1.1l.5 2.1h3.4l.5-2.1a7.5 7.5 0 0 0 2-1.1l2 .8 1.7-3-1.5-1.5a7.5 7.5 0 0 0 0-2.2l1.5-1.5-1.7-3-2 .8a7.5 7.5 0 0 0-2-1.1l-.5-2.1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="timer-overlay__settings">
          {/* Daily goal */}
          <div className="timer-overlay__setting-row">
            <span className="timer-overlay__setting-label">今日の目標</span>
            <div className="timer-overlay__goal-control">
              <input
                type="range"
                min="25"
                max="200"
                step="25"
                value={restSettings.dailyGoalMinutes}
                onChange={(e) => updateRestSettings({ dailyGoalMinutes: Number(e.target.value) })}
                className="timer-overlay__goal-slider"
              />
              <span className="timer-overlay__goal-value">{restSettings.dailyGoalMinutes}分</span>
            </div>
          </div>

          <div className="timer-overlay__setting-divider" />

          {/* Auto-start breaks */}
          <div className="timer-overlay__setting-row">
            <span className="timer-overlay__setting-label">休憩を自動開始</span>
            <button
              className={`timer-overlay__toggle ${pomodoroSettings.autoStartBreaks ? 'timer-overlay__toggle--on' : ''}`}
              onClick={() => updatePomodoroSettings({ autoStartBreaks: !pomodoroSettings.autoStartBreaks })}
            >
              <span className="timer-overlay__toggle-knob" />
            </button>
          </div>

          {/* Auto-start work */}
          <div className="timer-overlay__setting-row">
            <span className="timer-overlay__setting-label">作業を自動開始</span>
            <button
              className={`timer-overlay__toggle ${pomodoroSettings.autoStartPomodoros ? 'timer-overlay__toggle--on' : ''}`}
              onClick={() => updatePomodoroSettings({ autoStartPomodoros: !pomodoroSettings.autoStartPomodoros })}
            >
              <span className="timer-overlay__toggle-knob" />
            </button>
          </div>

          {/* Sound */}
          <div className="timer-overlay__setting-row">
            <span className="timer-overlay__setting-label">サウンド</span>
            <button
              className={`timer-overlay__toggle ${pomodoroSettings.soundEnabled ? 'timer-overlay__toggle--on' : ''}`}
              onClick={() => updatePomodoroSettings({ soundEnabled: !pomodoroSettings.soundEnabled })}
            >
              <span className="timer-overlay__toggle-knob" />
            </button>
          </div>

          {/* Notification */}
          <div className="timer-overlay__setting-row">
            <span className="timer-overlay__setting-label">通知</span>
            <button
              className={`timer-overlay__toggle ${pomodoroSettings.notificationEnabled ? 'timer-overlay__toggle--on' : ''}`}
              onClick={() => updatePomodoroSettings({ notificationEnabled: !pomodoroSettings.notificationEnabled })}
            >
              <span className="timer-overlay__toggle-knob" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const TimerOverlay: React.FC<TimerOverlayProps> = ({
  visible,
  onClose,
  timerDisplay,
  progressPercent,
  isRunning,
  phase,
  onStart,
  onStop,
}) => {
  const { pomodoroSettings, updatePomodoroSettings, resetTimer } = useAppStore();
  const { restSettings, updateRestSettings } = useRestStore();

  if (!visible) return null;
  const isIdle = !isRunning && !phase;
  const isStopped = !isRunning;
  const canClose = !isRunning || phase === 'break';

  // Determine which presets to show and current value
  const isBreakPhase = phase === 'break';
  const presets = isBreakPhase ? BREAK_PRESETS : WORK_PRESETS;
  const currentDuration = isBreakPhase
    ? pomodoroSettings.breakDuration
    : pomodoroSettings.workDuration;

  const handlePresetClick = (minutes: number) => {
    if (isBreakPhase) {
      updatePomodoroSettings({ breakDuration: minutes });
    } else {
      updatePomodoroSettings({ workDuration: minutes });
    }
    // 一時停止中のセッションを無警告で破棄しない（未開始時のみ表示に即反映）
    if (isIdle) {
      resetTimer();
    }
  };

  // Ring dimensions
  const radius = 110;
  const strokeWidth = 8;
  const size = (radius + strokeWidth) * 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = (1 - progressPercent / 100) * circumference;

  const phaseColor = phase === 'work'
    ? 'rgba(59, 130, 246, 0.8)'
    : phase === 'break'
      ? 'rgba(34, 197, 94, 0.6)'
      : 'rgba(59, 130, 246, 0.4)';

  const phaseLabel = phase === 'work' ? 'WORK' : phase === 'break' ? 'BREAK' : 'WORK';

  return (
    <div className="timer-overlay" onClick={canClose ? onClose : undefined}>
      <div className="timer-overlay__content" onClick={(e) => e.stopPropagation()}>
        {/* Phase label */}
        <div className="timer-overlay__phase">{phaseLabel}</div>

        {/* Large circular timer */}
        <div className="timer-overlay__circle" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="timer-overlay__svg">
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke="rgba(255, 255, 255, 0.08)"
              strokeWidth={strokeWidth}
              fill="none"
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={phaseColor}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="timer-overlay__progress"
            />
          </svg>
          <div className="timer-overlay__time">{timerDisplay}</div>
        </div>

        {/* Duration presets - only when timer is not running */}
        {isStopped && (
          <div className="timer-overlay__presets">
            {presets.map((min) => (
              <button
                key={min}
                onClick={() => handlePresetClick(min)}
                className={`timer-overlay__preset ${currentDuration === min ? 'timer-overlay__preset--active' : ''}`}
              >
                {min}
              </button>
            ))}
            <span className="timer-overlay__preset-unit">min</span>
          </div>
        )}

        {/* Settings toggle - only when timer is stopped */}
        {isStopped && (
          <SettingsPanel
            pomodoroSettings={pomodoroSettings}
            updatePomodoroSettings={updatePomodoroSettings}
            restSettings={restSettings}
            updateRestSettings={updateRestSettings}
          />
        )}

        {/* Control buttons */}
        <div className="timer-overlay__controls">
          {!isRunning ? (
            <>
              <button
                onClick={onStart}
                className="timer-overlay__btn timer-overlay__btn--start"
              >
                {phase && !isIdle ? 'Resume' : 'Start'}
              </button>
              <button
                onClick={onClose}
                className="timer-overlay__btn timer-overlay__btn--close"
              >
                Close
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onStop}
                className="timer-overlay__btn timer-overlay__btn--stop"
              >
                Stop
              </button>
              {phase === 'break' && (
                <button
                  onClick={onClose}
                  className="timer-overlay__btn timer-overlay__btn--close"
                >
                  Close
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimerOverlay;
